import throttle from 'lodash/throttle';

/**
 * Creates a timer that responds to the user being active and then backs off
 * when activity ceases. It fires at {options.min} ms when active, and, after
 * {options.inactivity} ms of inactivity, will fire at {options.max} ms.
 * No polling may happen while the document is hidden.
 */

const MINUTE_IN_MS = 60 * 1000;

/**
 * Timing configuration: note that we run the timer much quicker for the
 * integration test; partly because its default polling interval is already the
 * length of the test timeout. But be careful not to make these too short: the
 * poller won't work when the timer interval is shorter than the server
 * response time.
 */
const DEFAULT_OPTIONS = {
	min: 1 * MINUTE_IN_MS,
	max: 2 * MINUTE_IN_MS,
	inactivity: 2 * MINUTE_IN_MS,
};

const INTEGRATION_TEST_OPTIONS = {
	min: 4000,
	max: 4000,
	inactivity: 4000,
};

declare global {
	interface Window {
		__fastQuickReloadTimerForIntegrationTest?: boolean;
		__documentHiddenOverrideForUnitTest?: boolean;
	}
}

/**
 * Our TypeScript configuration currently has it validating against Node.js
 * types, not browser ones, and they have different return types for
 * setTimeout. This is the browser's type:
 */
declare function setTimeout(callback: (...args: any[]) => void, ms: number, ...args: any[]): number;

export class SmartTimer {
	private options: {
		min: number;
		max: number;
		inactivity: number;
	};
	private timer: number | undefined;
	private lastActivityAt: number;
	private lastCallbackAt: number;
	private nextCallbackAt: number;

	// Calculation helper methods

	private isDocumentHidden() {
		if (typeof window.__documentHiddenOverrideForUnitTest === 'boolean') {
			return window.__documentHiddenOverrideForUnitTest;
		}
		// document.hidden is read-only so we can't force it for unit testing
		return document.hidden;
	}

	/**
	 * The single, definitive interval calculator. Returns the timestamp at which
	 * the callback ought next be called, which may be in the past, given...
	 *
	 *   • how long ago the callback was last called
	 *   • how long ago activity was last observed
	 */
	private calculateNextDue(): number {
		const timeSinceLastActivity = Date.now() - this.lastActivityAt;

		const prescribedDelay =
			timeSinceLastActivity >= this.options.inactivity ? this.options.max : this.options.min;

		return this.lastCallbackAt + prescribedDelay;
	}

	// Timer set/adjust methods

	private setTimerToFireAt(timestamp: number) {
		if (this.timer !== undefined) {
			clearTimeout(this.timer);
		}

		const now = Date.now();
		const delay = Math.max(0, timestamp - now);

		this.timer = setTimeout(this.timerDidFire, delay);
		this.nextCallbackAt = now + delay;
	}

	private bringTimerForwardTo(timestamp: number) {
		if (this.nextCallbackAt > timestamp) this.setTimerToFireAt(timestamp);
	}

	// Event handlers (carefully bound to 'this', note)

	private timerDidFire = () => {
		this.timer = undefined;
		this.executeCallback();
		this.setTimerFromCold();
	};

	private didReceiveActivity = throttle(() => {
		this.lastActivityAt = Date.now();
		this.bringTimerForwardTo(this.calculateNextDue());
	}, 500);

	private visibilityDidChange = () => {
		if (this.isDocumentHidden()) {
			if (this.timer !== undefined) clearTimeout(this.timer);
			this.timer = undefined;
			this.nextCallbackAt = Infinity;
		} else {
			// Consider the visibility change to be an activity
			this.lastActivityAt = Date.now();
			this.setTimerFromCold();
		}
	};

	// Miscellaneous other methods

	/**
	 * Runs the callback independently, on the next tick, so that if it chokes
	 * or throws, the timer logic is isolated.
	 */
	private executeCallback() {
		setTimeout(this.callback, 0);
		this.lastCallbackAt = Date.now();
	}

	private setTimerFromCold() {
		this.setTimerToFireAt(this.calculateNextDue());
	}

	// Public methods

	constructor(public callback: () => void) {
		this.options = { ...DEFAULT_OPTIONS };

		if (window.__fastQuickReloadTimerForIntegrationTest) {
			// Integration test needs to run quickly....
			this.options = {
				...this.options,
				...INTEGRATION_TEST_OPTIONS,
			};
			// ...and needs to signal to the test runner that it can proceed to make
			// an update
			const testMarker = document.createElement('div');
			testMarker.setAttribute('data-test-id', 'quick-reload-test-marker');
			document.body.appendChild(testMarker);
		}

		this.lastActivityAt = 0;
		this.lastCallbackAt = 0;
		this.nextCallbackAt = 0;
	}

	start(fetchOnStart = false) {
		window.addEventListener('focus', this.didReceiveActivity);
		document.addEventListener('visibilitychange', this.visibilityDidChange);
		document.addEventListener('click', this.didReceiveActivity);
		document.addEventListener('scroll', this.didReceiveActivity);

		const now = Date.now();

		// Assume that we're starting the timer right after loading the page,
		// from a fetching perspective it's as if the callback just happened.
		// unless prescribed to fire immediately
		this.lastCallbackAt = fetchOnStart ? 0 : now;
		this.lastActivityAt = fetchOnStart ? 0 : now;
		this.nextCallbackAt = fetchOnStart ? 0 : Infinity;
		this.setTimerFromCold();
	}

	stop() {
		window.removeEventListener('focus', this.didReceiveActivity);
		document.removeEventListener('visibilitychange', this.visibilityDidChange);
		document.removeEventListener('click', this.didReceiveActivity);
		document.removeEventListener('scroll', this.didReceiveActivity);

		clearTimeout(this.timer);
		this.timer = undefined;
	}
}
